import classnames from 'classnames';
import type { UICompendium } from '@sorrem-client/types';
import { Icon } from '@sorrem/ui';
import { useAppNavigate } from '@sorrem-client/ui/hooks';
import {
    findCompendiumPageNumber,
    listCompendiumReadingOrder,
    findCompendiumChapter,
    findCompendiumVision,
    toVisionRelivePath,
    type CompendiumSelection,
} from '../../compendium-pages';
import { VisionPlate } from '../VisionPlate';
import { CompendiumContents } from './CompendiumContents';
import { FieldJournal, JournalPage, JournalRibbon, PageHeading, PageNote } from '../FieldJournal';
import styles from './CompendiumBook.module.scss';

interface CompendiumBookProps {
    compendium: UICompendium;
    /** Already resolved: null only for an empty Compendium. */
    selection: CompendiumSelection | null;
    onSelect: (selection: CompendiumSelection) => void;
}

/**
 * The Compendium, written in the back of the character's field journal: its contents on the left page, where
 * the player arranges their visions into chapters, and the selected chapter's
 * title page or vision's page on the right.
 */
export function CompendiumBook({ compendium, selection, onSelect }: CompendiumBookProps): JSX.Element {
    return (
        <FieldJournal
            className={styles.compendiumBook}
            left={<CompendiumContents compendium={compendium} selection={selection} onSelect={onSelect} />}
            right={selection === null ? (
                <PageNote>
                    No visions yet. When a Crucible&apos;s heart shatters, a memory may surface to you, and it will be kept here.
                </PageNote>
            ) : selection.kind === 'chapter' ? (
                <ChapterTitlePage compendium={compendium} chapterId={selection.chapterId} />
            ) : (
                <VisionPage key={selection.visionId} compendium={compendium} visionId={selection.visionId} onSelect={onSelect} />
            )}
        />
    );
}

/** A chapter's title page: a frontispiece from its first vision's plate, its name and its page count. */
function ChapterTitlePage({ compendium, chapterId }: { compendium: UICompendium; chapterId: string }): JSX.Element | null {
    const chapter = findCompendiumChapter(compendium, chapterId);
    if (chapter === undefined) {
        return null;
    }
    const frontispiece = chapter.visions.find((vision) => vision.plateUrl !== undefined) ?? chapter.visions[0];
    const pages = chapter.visions.length;

    return (
        <JournalPage>
            {frontispiece !== undefined ? (
                <VisionPlate className={styles.frontispiece} vision={frontispiece} isInked />
            ) : (
                <PageNote>Drag pages here from the contents to gather them into this chapter.</PageNote>
            )}
            <PageHeading title={chapter.name} meta={pages === 0 ? 'No pages yet' : pages === 1 ? '1 page' : `${pages} pages`} />
        </JournalPage>
    );
}

interface VisionPageProps {
    compendium: UICompendium;
    visionId: string;
    onSelect: (selection: CompendiumSelection) => void;
}

/** A vision's page: its plate and its text, with Relive and the page turns to the visions either side of it. */
function VisionPage({ compendium, visionId, onSelect }: VisionPageProps): JSX.Element | null {
    const navigate = useAppNavigate();
    const vision = findCompendiumVision(compendium, visionId);
    if (vision === undefined) {
        return null;
    }
    const order = listCompendiumReadingOrder(compendium);
    const index = order.findIndex((entry) => entry.visionId === visionId);
    const previous = order[index - 1];
    const next = order[index + 1];

    return (
        <JournalPage>
            <VisionPlate className={styles.plate} vision={vision} isInked />
            <PageHeading title={vision.title} />
            <div className={styles.visionText}>
                {vision.text.map((paragraph, paragraphIndex) => <p key={paragraphIndex}>{paragraph}</p>)}
            </div>

            <JournalRibbon onClick={() => navigate(toVisionRelivePath(vision.visionId))}>Relive</JournalRibbon>

            <footer className={styles.pageTurns}>
                <button
                    type="button"
                    className={classnames(styles.pageTurn, previous === undefined && styles.hidden)}
                    disabled={previous === undefined}
                    aria-label="Previous page"
                    onClick={() => previous && onSelect({ kind: 'vision', visionId: previous.visionId })}
                >
                    <Icon icon='arrow-left' size='large' />
                </button>
                <span className={styles.pageNumber}>Page {findCompendiumPageNumber(compendium, visionId)}</span>
                <button
                    type="button"
                    className={classnames(styles.pageTurn, next === undefined && styles.hidden)}
                    disabled={next === undefined}
                    aria-label="Next page"
                    onClick={() => next && onSelect({ kind: 'vision', visionId: next.visionId })}
                >
                    <Icon icon='arrow-right' size='large' />
                </button>
            </footer>
        </JournalPage>
    );
}
